// Reader-only fetch wrapper.
//
// Sends the device bearer token from localStorage as Authorization. If no
// token is present yet, falls back to credentials: 'include' so cookie-based
// sessions still work (admin debugging in the browser, or the very first
// visit before the device install completes).
//
// Tokens are stored PER USER (keyed by userId). The platform supports
// multi-account sessions via `lingvish_active_user` cookie + per-user
// `lingvish_access_${uid}` cookies — the reader token follows the same
// pattern so that switching accounts (or the family scenario: mom + kids on
// one device) never reuses someone else's bearer.
//
// Returns the same shape as fetch() — caller still does .ok / .json().

import { API } from '../config/api';

const TOKEN_KEY_PREFIX = 'reader_device_token:';
const LAST_USER_KEY = 'reader_last_user_id';
// Pre-multi-account single key. Kept for read-only fallback for installed
// PWAs upgrading past this fix. New writes never touch it.
const LEGACY_TOKEN_KEY = 'reader_device_token';
const ACTIVE_USER_COOKIE = 'lingvish_active_user';

function readActiveUserCookie() {
  if (typeof document === 'undefined') return null;
  const prefix = `${ACTIVE_USER_COOKIE}=`;
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (const part of parts) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
  }
  return null;
}

function safeGet(key) {
  try { return localStorage.getItem(key); } catch (_) { return null; }
}
function safeSet(key, value) {
  try { localStorage.setItem(key, value); } catch (_) {}
}
function safeRemove(key) {
  try { localStorage.removeItem(key); } catch (_) {}
}

// Returns the token for the currently-active user, or null.
//
// Priority:
//   1. `lingvish_active_user` cookie → per-user key. If missing, DO NOT fall
//      back — returning someone else's token is the exact bug we're fixing.
//   2. No cookie session (installed PWA whose cookie has expired) → use the
//      last known reader user in localStorage.
//   3. Very old installs that predate per-user storage → the legacy single
//      key, but only when neither cookie nor LAST_USER_KEY exist.
export const getReaderToken = () => {
  const cookieUid = readActiveUserCookie();
  if (cookieUid) {
    return safeGet(TOKEN_KEY_PREFIX + cookieUid);
  }
  const lastUid = safeGet(LAST_USER_KEY);
  if (lastUid) {
    const perUser = safeGet(TOKEN_KEY_PREFIX + lastUid);
    if (perUser) return perUser;
  }
  return safeGet(LEGACY_TOKEN_KEY);
};

// Writes the token for the given user (or the currently active user). Passing
// token=null clears just that user's entry.
export const setReaderToken = (token, userId) => {
  const uid = userId || readActiveUserCookie() || safeGet(LAST_USER_KEY);
  if (uid) {
    const key = TOKEN_KEY_PREFIX + uid;
    if (token) {
      safeSet(key, token);
      safeSet(LAST_USER_KEY, uid);
    } else {
      safeRemove(key);
      if (safeGet(LAST_USER_KEY) === uid) safeRemove(LAST_USER_KEY);
    }
  }
  // Any explicit clear also purges the pre-fix single-key token — a stale
  // legacy entry could otherwise resurface next time getReaderToken() runs.
  if (!token) safeRemove(LEGACY_TOKEN_KEY);
};

export const clearReaderToken = (userId) => setReaderToken(null, userId);

// Wipe the pre-fix single-key legacy token. Called during install so upgrades
// don't leave a dangling entry that could resurface if per-user storage is
// ever cleared.
const clearLegacyToken = () => safeRemove(LEGACY_TOKEN_KEY);

// Issue a fresh device token using the user's current cookie session.
// Called once on first authenticated visit to /reader, and the result
// stored in localStorage under the per-user key for future requests.
//
// `userId` should be the current platform user's id (from AuthContext). If
// omitted we fall back to the active-user cookie.
export const installReaderDevice = async (userId) => {
  try {
    const uid = userId || readActiveUserCookie();
    if (!uid) return null; // Cookie session missing — nothing to install against.
    const res = await fetch(`${API.READER}/auth/install`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        device_label: typeof navigator !== 'undefined' ? navigator.platform : null,
      }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data?.token) {
      setReaderToken(data.token, uid);
      clearLegacyToken();
      return data.token;
    }
    return null;
  } catch (_) {
    return null;
  }
};

// Revoke the given token on the server (or the current one if omitted), then
// clear it locally. Handles the pre-fix legacy token too so a stray old token
// can't outlive its revoke call.
export const revokeReaderDevice = async (options = {}) => {
  const explicitToken = options.token || null;
  const userId = options.userId;
  const token = explicitToken || getReaderToken();
  if (!token) {
    clearReaderToken(userId);
    return true;
  }
  try {
    await fetch(`${API.READER}/auth/revoke`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (_) {}
  if (explicitToken) {
    // Best-effort cleanup of whichever key held it.
    if (safeGet(LEGACY_TOKEN_KEY) === explicitToken) clearLegacyToken();
  } else {
    clearReaderToken(userId);
    clearLegacyToken();
  }
  return true;
};

// Wrapped fetch — adds the Authorization header if we have a token,
// and falls back to cookies otherwise.
export const readerFetch = (path, options = {}) => {
  const token = getReaderToken();
  const headers = { ...(options.headers || {}) };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const url = path.startsWith('http') ? path : `${API.READER}${path}`;
  return fetch(url, {
    ...options,
    headers,
    // Send cookies only if there's no bearer token — keeps the request
    // header smaller when the PWA already has a token.
    credentials: token ? 'omit' : 'include',
  });
};
